import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, ConfirmationDialog, StatusBadge, TradingStrategySelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import StartSweepModal from './components/StartSweepModal';
import {
    buildTuningRuns,
    formatStudyStatus,
    formatTimestamp,
    getStudyStatusTone,
    isEntryOptimizerJobActive,
    isEntryOptimizerStudyActive,
    isEnvelopeRunActive,
    isTuningRunActive,
    parsePositiveInt,
    type EntryOptimizerEnvelopeRun,
    type TuningRun
} from './entryOptimizerUtils';
import type { TradingStrategy } from '@einhaus/project314-types';

/**
 * Entry Tuning home: leads with the outcome of the most recent run, then lists
 * every tuning run as ONE row (the internal data-collection + tuning stages are
 * collapsed into a single story-status). Variation searches get their own
 * section. No optimizer jargon on this page.
 */
const EntryOptimizer = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pendingDeleteRun, setPendingDeleteRun] = useState<TuningRun | null>(null);
    const [tuneTarget, setTuneTarget] = useState<EntryOptimizerEnvelopeRun | null>(null);

    const requestedStrategyId = parsePositiveInt(searchParams.get('strategyId'));

    const studiesQuery = useAppQuery({
        ...trpc.entryOptimizer.listStudies.queryOptions({}),
        refetchInterval: (query) => ((query.state.data?.studies ?? []).some(isEntryOptimizerStudyActive) ? 15000 : false),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const jobsQuery = useAppQuery({
        ...trpc.entryOptimizer.listJobs.queryOptions({}),
        refetchInterval: (query) => ((query.state.data?.jobs ?? []).some(isEntryOptimizerJobActive) ? 5000 : false),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const envelopeRunsQuery = useAppQuery({
        ...trpc.entryOptimizer.listEnvelopeRuns.queryOptions({}),
        refetchInterval: (query) => ((query.state.data?.runs ?? []).some(isEnvelopeRunActive) ? 5000 : false),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const deleteJobMutation = useMutation({ ...trpc.entryOptimizer.deleteJob.mutationOptions() });
    const deleteExperimentMutation = useMutation({ ...trpc.experiment.delete.mutationOptions() });

    const [isDeleting, setIsDeleting] = useState(false);

    // A tuning run spans up to two records: the data-collection experiment
    // (always present) and the tuning job (only once tuning ran). Remove the job
    // first, then the experiment, so a run that failed at EITHER stage clears.
    const deleteTuningRun = async (tuningRun: TuningRun) => {
        setIsDeleting(true);

        try {
            if (tuningRun.job) {
                await deleteJobMutation.mutateAsync({ jobId: tuningRun.job.id });
            }

            await deleteExperimentMutation.mutateAsync({ experimentId: tuningRun.run.experimentId });

            showToast('Tuning run deleted', 'success');
            setPendingDeleteRun(null);

            await Promise.all([
                queryClient.invalidateQueries(trpc.entryOptimizer.listJobs.pathFilter()),
                queryClient.invalidateQueries(trpc.entryOptimizer.listEnvelopeRuns.pathFilter())
            ]);
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Failed to delete tuning run', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data?.jobs]);
    const envelopeRuns = useMemo(() => envelopeRunsQuery.data?.runs ?? [], [envelopeRunsQuery.data?.runs]);
    const studies = studiesQuery.data?.studies ?? [];

    const tuningRuns = useMemo(() => buildTuningRuns(envelopeRuns, jobs), [envelopeRuns, jobs]);

    const filteredRuns = useMemo(
        () => tuningRuns.filter((tuningRun) => requestedStrategyId === null || tuningRun.run.strategyId === requestedStrategyId),
        [tuningRuns, requestedStrategyId]
    );

    const filteredStudies = useMemo(
        () => studies.filter((study) => requestedStrategyId === null || study.strategyId === requestedStrategyId),
        [studies, requestedStrategyId]
    );

    const availableStrategyIds = useMemo(() => {
        const ids = new Set<number>();

        for (const tuningRun of tuningRuns) ids.add(tuningRun.run.strategyId);
        for (const study of studies) ids.add(study.strategyId);

        return ids;
    }, [tuningRuns, studies]);

    const isStrategyInResults = useCallback((strategy: TradingStrategy) => availableStrategyIds.has(strategy.id), [availableStrategyIds]);

    // The answer-first headline: the newest run that has something to say.
    const latestMeaningful = useMemo(
        () => filteredRuns.find((tuningRun) => isTuningRunActive(tuningRun) || tuningRun.phase === 'done') ?? null,
        [filteredRuns]
    );

    const updateStrategyFilter = (strategyId: number | null) => {
        const nextParams = new URLSearchParams(searchParams);

        if (strategyId !== null) nextParams.set('strategyId', String(strategyId));
        else nextParams.delete('strategyId');

        setSearchParams(nextParams);
    };

    const loading = jobsQuery.isLoading || envelopeRunsQuery.isLoading;

    return (
        <UserLayout containerSize="1100px" title="Entry Tuning - Project 314">
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-zinc-100">Entry Tuning</h1>
                    <p className="mt-1 text-sm text-zinc-400">
                        Test better settings for a strategy&apos;s entry logic and apply the ones that beat what you have now.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => Promise.all([jobsQuery.refetch(), envelopeRunsQuery.refetch(), studiesQuery.refetch()])}
                    >
                        Refresh
                    </Button>
                    <Button size="sm" to="/user/entryOptimizer/studies/new" variant="outline-dark">
                        Variation Search
                    </Button>
                    <Button size="sm" to="/user/entryOptimizer/new">
                        Tune an Entry
                    </Button>
                </div>
            </div>

            {latestMeaningful && <OutcomeBanner tuningRun={latestMeaningful} onRunTuning={() => setTuneTarget(latestMeaningful.run)} />}

            {availableStrategyIds.size > 0 && (
                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-[260px]">
                    <TradingStrategySelect
                        filter={isStrategyInResults}
                        label="Filter by strategy"
                        placeholder="All strategies"
                        value={requestedStrategyId}
                        onChange={updateStrategyFilter}
                    />
                </div>
            )}

            <Card subtitle="Each run tests alternative entry settings and reports what beats your current entry." title="Tuning Runs">
                {loading ? (
                    <DataLoadingSpinner text="Loading tuning runs..." />
                ) : filteredRuns.length === 0 ? (
                    <div className="rounded-md border border-zinc-800 bg-black/20 p-6 text-center text-sm text-zinc-500">
                        No tuning runs yet. Click <span className="text-zinc-300">Tune an Entry</span> to start one.
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {filteredRuns.map((tuningRun) => (
                            <TuningRunRow
                                key={tuningRun.run.experimentId}
                                tuningRun={tuningRun}
                                onDelete={() => setPendingDeleteRun(tuningRun)}
                                onRunTuning={() => setTuneTarget(tuningRun.run)}
                            />
                        ))}
                    </div>
                )}
            </Card>

            {filteredStudies.length > 0 && (
                <Card
                    className="mt-4"
                    subtitle="Searches that compare whole entry variations (e.g. different moving-average periods), not just settings."
                    title="Variation Searches"
                >
                    <div className="flex flex-col gap-2">
                        {filteredStudies.map((study) => (
                            <Link
                                className="flex items-center justify-between rounded-md border border-zinc-800 bg-black/20 p-3 hover:border-zinc-700"
                                key={study.id}
                                to={`/user/entryOptimizer/studies/${study.id}`}
                            >
                                <div>
                                    <div className="text-sm font-medium text-zinc-100">
                                        Variation search #{study.id} · strategy #{study.strategyId}
                                    </div>
                                    <div className="mt-0.5 text-xs text-zinc-500">
                                        Started {formatTimestamp(study.createdAt)}
                                        {study.verdict === 'variant_recommended'
                                            ? ' · a variation beat your current entry'
                                            : study.verdict === 'keep_incumbent'
                                              ? ' · current entry held up'
                                              : ''}
                                    </div>
                                </div>
                                <StatusBadge status={study.status} tone={getStudyStatusTone(study.status)}>
                                    {formatStudyStatus(study.status)}
                                </StatusBadge>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            <StartSweepModal
                run={tuneTarget}
                onClose={() => setTuneTarget(null)}
                onCreated={async (jobId) => {
                    setTuneTarget(null);
                    await queryClient.invalidateQueries(trpc.entryOptimizer.listJobs.pathFilter());
                    void navigate(`/user/entryOptimizer/${jobId}`);
                }}
            />

            <ConfirmationDialog
                confirmText="Delete run"
                isConfirmLoading={isDeleting}
                isOpen={pendingDeleteRun !== null}
                message="Delete this tuning run? Its collected data and any results are removed. This can't be undone."
                title="Delete tuning run"
                onCancel={() => setPendingDeleteRun(null)}
                onConfirm={() => {
                    if (pendingDeleteRun) {
                        deleteTuningRun(pendingDeleteRun).catch(() => undefined);
                    }
                }}
            />
        </UserLayout>
    );
};

/** Answer-first headline for the most recent run. */
const OutcomeBanner = ({ tuningRun, onRunTuning }: { tuningRun: TuningRun; onRunTuning: () => void }) => {
    if (tuningRun.phase === 'preparing' || tuningRun.phase === 'tuning') {
        return (
            <Callout className="mb-4" variant="info">
                Tuning in progress — {tuningRun.statusLabel.toLowerCase()} This usually takes a little while; results appear here when it
                finishes.
            </Callout>
        );
    }

    if (tuningRun.phase === 'ready') {
        return (
            <Callout className="mb-4" variant="warning">
                A data-collection run is ready.{' '}
                <button className="font-medium text-cyan-300 hover:underline" type="button" onClick={onRunTuning}>
                    Run tuning now
                </button>{' '}
                to test alternative settings against it.
            </Callout>
        );
    }

    if (tuningRun.phase === 'done' && (tuningRun.improvements ?? 0) > 0 && tuningRun.job) {
        return (
            <Callout className="mb-4" variant="success">
                Your last tuning run found <span className="font-semibold">{tuningRun.improvements}</span> setting
                {tuningRun.improvements === 1 ? '' : 's'} that beat your current entry.{' '}
                <Link className="font-medium text-cyan-300 hover:underline" to={`/user/entryOptimizer/${tuningRun.job.id}`}>
                    See what changed
                </Link>
            </Callout>
        );
    }

    if (tuningRun.phase === 'done') {
        return (
            <Callout className="mb-4" variant="info">
                Your last tuning run found no improvement — your current entry settings are already strong.
            </Callout>
        );
    }

    return null;
};

/** One logical tuning run: data-collection + tuning collapsed into a single row. */
const TuningRunRow = ({ tuningRun, onRunTuning, onDelete }: { tuningRun: TuningRun; onRunTuning: () => void; onDelete: () => void }) => {
    const { run, job, phase } = tuningRun;

    // The cloned experiment carries the source name; strip the internal suffix so
    // runs are told apart by the universe they used, not just the strategy.
    const sourceLabel = run.name.replace(/\s*\[entry optimizer envelope\].*$/i, '').trim();

    return (
        <div className="flex flex-col gap-2 rounded-md border border-zinc-800 bg-black/20 p-3 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
                <div className="truncate font-medium text-zinc-100">
                    {sourceLabel ? `${sourceLabel} — entry tuning` : `Strategy #${run.strategyId} entry tuning`}
                </div>
                <div className="mt-0.5 text-xs text-zinc-500">
                    Strategy #{run.strategyId} · {run.durations.join(', ')} · {formatTimestamp(run.createdAt)} · run #{run.experimentId}
                </div>
                {run.error && <div className="mt-1 text-xs text-red-300">{run.error}</div>}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3">
                <StatusBadge status={phase} tone={tuningRun.statusTone}>
                    {tuningRun.statusLabel}
                </StatusBadge>
                {phase === 'ready' && (
                    <Button size="sm" onClick={onRunTuning}>
                        Run tuning
                    </Button>
                )}
                {job && (
                    <Button size="sm" to={`/user/entryOptimizer/${job.id}`} variant="outline-dark">
                        View results
                    </Button>
                )}
                {(phase === 'done' || phase === 'failed') && (
                    <Button size="sm" variant="destructive" onClick={onDelete}>
                        Delete
                    </Button>
                )}
            </div>
        </div>
    );
};

export default EntryOptimizer;
